import { Component, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { NotifyService } from '@app/Services/NotifyService';
import { InputDataUnit } from '@app/Global/Models/ClientModels';
import { ConfirmModal } from '@app/Components/Loan/ConfirmModal/ConfirmModal';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@app/Services/APIService';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { AccountsControllerMethods, PaymentManagementControllerMethods } from '@app/Global/EnumManager';

@Component({
    selector: 'PaymentManagementModify',
    templateUrl: './PaymentManagementModify.html',
    styleUrls: ['./PaymentManagementModify.scss'],
    animations: [
        trigger('fadeInSection', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('0.3s ease-in', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class PaymentManagementModify implements OnInit {

    // Modal Identifier
    public ModalIdentifier: string;

    //New ModelData that captures it inside an array, which lets us use a generic function to retrieve and update
    public ModelData: InputDataUnit[] = [];

    //Payment Type Description Message
    public PaymentTypeDescriptionMessage: string = "";

    // Payment Frequency Options. Removed "Once" as part of XV8-5808
    public FrequencyOptions = [{ ControlGUID: "Weekly", ControlValue: "Weekly" }, { ControlGUID: "Fortnightly", ControlValue: "Fortnightly" }, { ControlGUID: "Monthly", ControlValue: "Monthly" }];

    //Payment Frequency Options
    public TransferTypeOptions = [{ ControlGUID: "One-Off", ControlValue: "One-Off" },{ ControlGUID: "Recurring", ControlValue: "Recurring" }];
    
    public NewFrequencyValue = "";
    //Payment Transaction Options
    public PaymentTransactionOptions: any;

    //Charge Transaction Options
    public ChargeTransactionOptions: any;

    //Customer Nmominated Frequency Options
    public CustomerNominatedFrequencyOptions: any;

    //Frequency GUID
    public MonthlyPaymentFrequencyGUID: any;

    //Available Frequency Options for Change Frequency feature - excludes 'One Off' and the current frequency
    public AvailableFrequencyOptions = [];

    //Target Account ID
    public TargetAccountID;

    //To store the linked account for the transfer
    public TransferFromAccountOptions = []
    public TransferToAccountOptions = []

    //Linked account details, this get populated from the parent component
    public LinkedAccountDetails: any;

    //Max Allowable Date for the date picker
    public DTPMaxDate = new Date(2050, 1, 1);

    //Today's date
    public DTPDate = new Date();

    // DateTime min date, ignoring the timestamp, see XV8-5794
    public DTPMinDate = new Date(this.DTPDate.getFullYear(), this.DTPDate.getMonth(), this.DTPDate.getDate());

    // DateTime min date plus 1 day, ignoring the timestamp, see XV8-5808
    public DTPMinDatePlusOneDay = new Date(this.DTPMinDate.getFullYear(), this.DTPMinDate.getMonth(), this.DTPMinDate.getDate() + 1);

    //This is used to determine if the Inter Account Transfer Modify section should be shown
    public ShowInterAccountTransferModify = false;

    //This is used to determine if the Bank Account Modify section should be shown
    public ShowBankAccountModify = false;

    //This isused to determine if the Customer Nominated Payment section should be shown
    public ShowCustomerNominatedPaymentModify = false;

    //This is used to determine if the Scheduled Payment section should be shown
    public ShowScheduledPaymentModify = false;
    public ShowMinimumPaymentAmount = true;
    public ShowCustomerNominatedInput = false;
    public ShowAdditionalPaymentInput = false;
    public ShowTotalDDAmount = false;

    //This is used to determine if the Defer Payment section should be shown
    public ShowDeferPaymentModify = false;

    //This is used to determine if the Change Frequency section should be shown
    public ShowChangeFrequencyModify = false;

    //Bank account details, this can be populated from the parent component
    public BankAccountDetails: any;

    //Scheduled payment details, this gets populated from the parent component
    public ScheduledPaymentDetails = [];

    //Selected payment details for editing
    public SelectedPaymentDetails: any;

    //Existing scheduled payment
    public ExistingScheduledPayment: any;

    //This is used to determine if the transfer frequency should be shown
    public ShowTransferFrequency = true;

    //This is used to determine if the expiry date should be shown
    public ShowTransferExpiryDate = true;

    //This is used to determine if the payment expiry date should be shown
    public ShowPaymentExpiryDate = false;

    //Payment Methods
    public PaymentMethodOptions: any;

    //Schedule Frequency Options
    public ScheduleFrequencyOptions: any;

    //Monthly Instalment Amount
    public MonthlyInstalmentAmount: number;

    //Repayment Amounts
    public MonthlyRepaymentAmount: number;
    public FortnightlyRepaymentAmount: number;
    public WeeklyRepaymentAmount: number;

    public MonthlyRepaymentAmountFormatted: string = "";
    public FortnightlyRepaymentAmountFormatted: string = "";
    public WeeklyRepaymentAmountFormatted: string = "";

    //Formatted Monthly Instalment Amount
    public MonthlyInstalmentAmountFormatted: string = "";

    //Schedule Payment Frequency
    public NewSchedulePaymentFrequency: string = "";

    //Schedule Payment Payment Due Date
    public NewInstalmentDueDate: string = "";

    //Monthly Instalment Due Date
    public InstalmentDueDate: string = "";

    // Create options for defer months (1-5)
    public DeferMonthsOptions = [
        { ControlGUID: "1", ControlValue: "1 Month" },
        { ControlGUID: "2", ControlValue: "2 Months" },
        { ControlGUID: "3", ControlValue: "3 Months" },
        { ControlGUID: "4", ControlValue: "4 Months" },
        { ControlGUID: "5", ControlValue: "5 Months" }
    ];

    //Page Title
    public PageTitle: string = "Payment Management Modify";

    //Modify Type, this is used to determine which modify section to show
    public ModifyType = "";

    //This is to store the current BSB number value, used for autocomplete and validation
    public CurrentBSBNumberValue = "";

    //For tracking if a server update request is in progress
    public IsGenerateInProgress = false;

    //Constructor
    constructor(
        private apiService: ApiService,
        public globalFunctions: GlobalFunctions,
        private notifyService: NotifyService,
        private dialog: MatDialog,
        private clientDataStore: ClientDataStore
    ) { }

    //This is where we will initialize the component and set the ModifyType based on the input
    ngOnInit(): void {

        // Component initialization

        //Check if the ModifyType is InterAccountTransfer
        if (this.ModifyType === "ProcessInterAccountTransfer") {
            // XV8-5810 Show "Immediate" Option based on claim check
            if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "ImmediateInterAccountTransfer", "Read") === true) {
                this.TransferTypeOptions.unshift({ ControlGUID: "Immediate", ControlValue: "Immediate" });
            }

            //Show the Inter Account Transfer Modify section
            this.ShowInterAccountTransferModify = true;

            //Fill static data for the input data model for InterAccountTransfer here, base it on the input names. Demonstrating the use of the partial constructor here, only mapping the properties we want to use
            this.ModelData.push(
                new InputDataUnit({ Name: "INP_TransferFrom", DisplayName: "Transfer From", Placeholder: "Choose from account", HTMLInputType: "dropdown", Type: "string" })
                , new InputDataUnit({ Name: "INP_TransferTo", DisplayName: "Transfer To", Placeholder: "Choose to account", HTMLInputType: "dropdown", Type: "string" })
                , new InputDataUnit({ Name: "INP_Frequency", DisplayName: "Frequency", Placeholder: "Choose a frequency", HTMLInputType: "dropdown", Type: "string" })
                , new InputDataUnit({ Name: "INP_TransferType", DisplayName: "Transfer Type", Placeholder: "Choose a transfer type", HTMLInputType: "dropdown", Type: "string" })
                , new InputDataUnit({ Name: "INP_TransferAmount", DisplayName: "Amount", Placeholder: "Input an amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric })
                , new InputDataUnit({ Name: "INP_DueDate", DisplayName: "Start Date", Placeholder: "Payment Start Date", HTMLInputType: "date", Type: "shortdate" })
                , new InputDataUnit({ Name: "INP_ExpiryDate", DisplayName: "Expiry Date", Placeholder: "Payment Expiry Date", HTMLInputType: "date", Type: "shortdate" })
                , new InputDataUnit({ Name: "INP_DueUntilFurtherNotice", LabelUnchecked: "Click to set recurring payment until further notice", LabelChecked: "Untick to set an expiry date", LabelDisplay: "Until Further Notice", HTMLInputType: "checkbox", Type: "string", Value: "false", IsChecked: false })
            );

            //Populate Transfer From and To Account Options
            if (!this.globalFunctions.isEmpty(this.LinkedAccountDetails)) {
                this.LinkedAccountDetails.forEach(linkedAccount => {
                    this.TransferFromAccountOptions.push({
                        ControlGUID: "" + linkedAccount.AccountID + "", ControlValue: linkedAccount.ProductType + " ID - " + linkedAccount.AccountID + " - Available " + linkedAccount.AvailableBalance_lbl
                    })

                    //Same for now
                    this.TransferToAccountOptions.push({
                        ControlGUID: "" + linkedAccount.AccountID + "", ControlValue: linkedAccount.ProductType + " ID - " + linkedAccount.AccountID + " - Available " + linkedAccount.AvailableBalance_lbl
                    })
                });
            }
        }

        // Check if the ModifyType is BankAccount
        else if (this.ModifyType === "EditBankAccount") {

            //Show the Bank Account Modify section
            this.ShowBankAccountModify = true;

            //See if we have bank account details passed from the parent component
            let bsbNumberValue = "";
            let accountNumberValue = "";
            let accountNameValue = "";

            //If we have existing bank account details, populate the form
            if (!this.globalFunctions.isEmpty(this.BankAccountDetails)) {
                bsbNumberValue = this.BankAccountDetails.AccountBSBNo;
                accountNumberValue = this.BankAccountDetails.AccountNumber;
                accountNameValue = this.BankAccountDetails.AccountName;

                //Set the current BSB number value for validation and autocomplete
                this.CurrentBSBNumberValue = bsbNumberValue;
            }

            //Initialize the bank account form fields
            this.ModelData.push(

                //TODO: AutocompleteControlData is hardcoded for now, we can change it later to use a service to fetch the autocomplete data
                new InputDataUnit({ Name: "INP_BSBNumber", DisplayName: "BSB Number", Placeholder: "Enter BSB Number", HTMLInputType: "autocomplete", Type: "string", Value: bsbNumberValue, ValueDisplay: bsbNumberValue, AutoCompleteControlData: { ControlDisplay: bsbNumberValue, ControlGUID: bsbNumberValue, ControlValue: bsbNumberValue, ControlType: "BSB No." } })

                , new InputDataUnit({ Name: "INP_AccountNumber", DisplayName: "Account Number", Placeholder: "Enter Account Number", HTMLInputType: "textarea", Type: "integer", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxIntegerSM, Value: accountNumberValue, ValueDisplay: accountNumberValue })

                , new InputDataUnit({ Name: "INP_AccountName", DisplayName: "Account Name", Placeholder: "Enter Account Name", HTMLInputType: "textarea", Type: "string", MinLength: this.globalFunctions.TextAreaValidLengthXS, MaxLength: this.globalFunctions.TextAreaValidLengthLG, Value: accountNameValue, ValueDisplay: accountNameValue })
            );
        }

        // Check if the ModifyType is CustomerNominatedPayment
        else if (this.ModifyType === "CustomerNominatedPayment") {

            //Show the Customer Nominated Payment section
            this.ShowCustomerNominatedPaymentModify = true;

            //Set default values
            const amountValue = "";
            const nextPaymentDateValue = null;
            const expiryDateValue = null;

            // Set a default value of Not Applicable when list is empty
            let chargeTransactionFiller = "";
            if (this.ChargeTransactionOptions.filter(a=>a.ControlGUID === "Not Applicable")){
                chargeTransactionFiller = "Not Applicable";
            }

            //Initialize the payment form fields
            this.ModelData.push(
                new InputDataUnit({ Name: "INP_PaymentAmount", DisplayName: "Amount", Placeholder: "Enter payment amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric, Value: amountValue, ValueDisplay: amountValue })
                , new InputDataUnit({ Name: "INP_PaymentFrequency", DisplayName: "Frequency", Placeholder: "Choose a frequency", HTMLInputType: "dropdown", Type: "string" })
                , new InputDataUnit({ Name: "INP_NextPaymentDate", DisplayName: "Next Payment Date", Placeholder: "Select next payment date", HTMLInputType: "date", Type: "shortdate", Value: nextPaymentDateValue, ValueDisplay: "" })
                , new InputDataUnit({ Name: "INP_PaymentExpiryDate", DisplayName: "Expiry Date", Placeholder: "Select expiry date", HTMLInputType: "date", Type: "shortdate", Value: expiryDateValue, ValueDisplay: "" })
                // , new InputDataUnit({ Name: "INP_PaymentMethod", DisplayName: "Payment Method", Placeholder: "Choose payment method", HTMLInputType: "dropdown", Type: "string", Value: paymentMethodValue, ValueDisplay: paymentMethodValue })
                , new InputDataUnit({ Name: "INP_UntilFurtherNotice", LabelUnchecked: "Click to set payment until further notice", LabelChecked: "Untick to set an expiry date", LabelDisplay: "Until Further Notice", HTMLInputType: "checkbox", Type: "string", Value: "false", IsChecked: false }),
                new InputDataUnit({ Name: "INP_PaymentTxn", DisplayName: "Payment Transaction (CR)", Placeholder: "Choose Payment Transaction", HTMLInputType: "dropdown", Type: "string" }),
                new InputDataUnit({ Name: "INP_ChargeTxn", DisplayName: "Charge Transaction (if applicable) (DR)", Placeholder: "Choose Charge Transaction", HTMLInputType: "dropdown", Type: "string", Value: chargeTransactionFiller }),
                new InputDataUnit({ Name: "INP_Note", DisplayName: "Note", Placeholder: "Note", HTMLInputType: "textarea", Type: "string", MinLength: this.globalFunctions.TextAreaValidLengthOptional, MaxLength: this.globalFunctions.TextAreaValidMaxLengthMD, IsResizable: true, CSSHeight: "glb_textAreaHeightMedium" })
            );
        }        //Check if the ModifyType is ScheduledPayment
        else if (this.ModifyType === "ScheduledPayment") {

            //Show the Scheduled Payment section
            this.ShowScheduledPaymentModify = true;

            //Format the Monthly Instalment Amount for display
            this.MonthlyInstalmentAmountFormatted = this.globalFunctions.customDataTypeParser(this.MonthlyInstalmentAmount, "currency.2", "aus");

            //Format the Repayment Amounts for display
            this.MonthlyRepaymentAmountFormatted = this.globalFunctions.customDataTypeParser(this.MonthlyRepaymentAmount, "currency.2", "aus");
            this.FortnightlyRepaymentAmountFormatted = this.globalFunctions.customDataTypeParser(this.FortnightlyRepaymentAmount, "currency.2", "aus");
            this.WeeklyRepaymentAmountFormatted = this.globalFunctions.customDataTypeParser(this.WeeklyRepaymentAmount, "currency.2", "aus");

            // Check for existing scheduled payments
            const existingScheduledPayment = this.ScheduledPaymentDetails.find(payment => payment.PaymentType === "Scheduled");
            if (!this.globalFunctions.isEmpty(existingScheduledPayment)) {
                this.ExistingScheduledPayment = existingScheduledPayment;
            }

            //Default to Monthly for scheduled payments (options are Weekly, Fortnightly, and Monthly only)
            const frequencyValue = this.NewSchedulePaymentFrequency;

            // Default payment type to Scheduled
            let scheduledPaymentMethod: any

            //Find the scheduled payment if available
            if (!this.globalFunctions.isEmpty(this.PaymentMethodOptions)) {
                scheduledPaymentMethod = this.PaymentMethodOptions.find(method => method.ControlGUID === "Scheduled")[0];
            }

            if (this.globalFunctions.isEmpty(scheduledPaymentMethod)) {

                //Default to Scheduled if we can't find it in the options for some reason
                scheduledPaymentMethod = {
                    ControlDisplay: "Scheduled", ControlGUID: "Scheduled",
                    ControlValue: "Scheduled", ControlType: "Scheduled Payment Method"
                }
            }

            //Set the default monthly payment frequency GUID
            let initialMonthlyPaymentFrequency: any
            if (!this.globalFunctions.isEmpty(this.ScheduleFrequencyOptions)) {
                initialMonthlyPaymentFrequency = this.ScheduleFrequencyOptions.find(option => option.ControlGUID === this.MonthlyPaymentFrequencyGUID);
            }

            //If empty, lets set it to a blank option
            if (this.globalFunctions.isEmpty(initialMonthlyPaymentFrequency)) {
                initialMonthlyPaymentFrequency = {
                    ControlDisplay: "", ControlGUID: "",
                    ControlValue: "", ControlType: "Scheduled Frequency"
                };
            }

            // Set the initial payment type description message for Scheduled payment type
            this.PaymentTypeDescriptionMessage = "Collect the minimum monthly instalment amount. Monthly instalment is divided by 2 for fortnightly and 4 for weekly frequencies.";

            //Hide the Customer Nominated Input and Additional Payment Input by default
            this.ShowCustomerNominatedInput = false;
            this.ShowAdditionalPaymentInput = false;
            this.ShowTotalDDAmount = false;
            this.NewFrequencyValue = frequencyValue;

            // Initialize the scheduled payment form fields with the new payment type dropdown at the top
            this.ModelData.push(
                new InputDataUnit({ Name: "INP_PaymentMethod", DisplayName: "Payment Method", Placeholder: "Choose payment Method", HTMLInputType: "dropdown", Type: "string", Value: scheduledPaymentMethod.ControlValue }),
                new InputDataUnit({ Name: "INP_DirectDebitAmount", DisplayName: "Direct Debit Amount", Placeholder: "Enter direct debit amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric, Value: this.MonthlyRepaymentAmount.toString(), ValueDisplay: this.MonthlyRepaymentAmountFormatted, Disabled: true }),
                new InputDataUnit({ Name: "INP_CustomerNominatedAmount", DisplayName: "Customer Nominated Amount", Placeholder: "Enter additional payment amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric, Value: "0", ValueDisplay: this.globalFunctions.customDataTypeParser(0, "currency.2", "aus") }),
                new InputDataUnit({ Name: "INP_AdditionalAmount", DisplayName: "Additional Amount", Placeholder: "Enter additional payment amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric, Value: "0", ValueDisplay: this.globalFunctions.customDataTypeParser(0, "currency.2", "aus") }),
                new InputDataUnit({ Name: "INP_TotalAmount", DisplayName: "Total Direct Debit Amount", Placeholder: "Total amount", HTMLInputType: "textarea", Type: "currency", MinValue: this.globalFunctions.TextAreaValidMinNumeric, MaxValue: this.globalFunctions.TextAreaValidMaxNumeric, Value: this.MonthlyRepaymentAmount.toString(), ValueDisplay: this.MonthlyRepaymentAmountFormatted, Disabled: true }),
                new InputDataUnit({
                    Name: "INP_ScheduledFrequency", DisplayName: "Frequency", Placeholder: "Choose a frequency", HTMLInputType: "dropdown", Type: "string", Value: initialMonthlyPaymentFrequency.ControlGUID
                }),
                new InputDataUnit({ Name: "INP_ScheduledNextPaymentDate", DisplayName: "Next Payment Date", Placeholder: "Select next payment date", HTMLInputType: "date", Type: "shortdate", ValueDate: this.NewInstalmentDueDate, Value: this.NewInstalmentDueDate }),
                new InputDataUnit({ Name: "INP_Note", DisplayName: "Note", Placeholder: "Note", HTMLInputType: "textarea", Type: "string", MinLength: this.globalFunctions.TextAreaValidLengthOptional, MaxLength: this.globalFunctions.TextAreaValidMaxLengthMD, IsResizable: true, CSSHeight: "glb_textAreaHeightMedium" })
            );
        }

        //Check if the ModifyType is DeferPayment
        else if (this.ModifyType === "DeferPayment") {

            //Show the Defer Payment section
            this.ShowDeferPaymentModify = true;

            //Default selected value
            const selectedDeferMonths = "1";

            //Initialize the defer payment form fields
            const deferMonthsInput = new InputDataUnit({
                Name: "INP_DeferMonths",
                DisplayName: "Defer Payment By",
                Placeholder: "Select number of months",
                HTMLInputType: "dropdown",
                Type: "string",
                Value: selectedDeferMonths,
                ValueDisplay: "1 Month"
            });

            this.ModelData.push(deferMonthsInput);
        }

        //Check if the ModifyType is ChangeFrequency
        else if (this.ModifyType === "ChangeFrequency") {

            //Show the Change Frequency section
            this.ShowChangeFrequencyModify = true;

            // Get the current frequency value from the selected payment
            let currentFrequencyGUID = "";
            if (!this.globalFunctions.isEmpty(this.SelectedPaymentDetails)) {
                currentFrequencyGUID = this.SelectedPaymentDetails.FrequencyTaskGUID;
            }

            // Filter out the 'One Off' option and current frequency from the available options
            this.AvailableFrequencyOptions = this.CustomerNominatedFrequencyOptions
                .filter(option => option.Classifications.toLowerCase().includes("one off") === false && option.ControlGUID !== currentFrequencyGUID);

            // Initialize the change frequency form fields
            const frequencyInput = new InputDataUnit({ Name: "INP_NewFrequency", DisplayName: "New Frequency", Placeholder: "Choose a frequency", HTMLInputType: "dropdown", Type: "string" })

            // Add the input to the ModelData array
            this.ModelData.push(frequencyInput);
        }
    }

    //Get the matching model data input, used in InputView_Sync to determine when new content needs to be rendered
    public ModelDataUnit_Get(input) {
        const modelDataItem = this.ModelData.filter(x => x.Name === input.id)[0];

        if (!this.globalFunctions.isEmpty(modelDataItem)) {
            return modelDataItem;
        }
    }

    //Sync the display of the UI input elements
    public InputView_Sync(): void {

        //TODO: Implement the logic to sync the input view based on the ModelData
        //A small delay so that user click still catches the correct UI element.
        this.globalFunctions.delay(200).then(() => {

            if (this.ShowInterAccountTransferModify) {

                //Set the ShowTransferFrequency and ShowTransferExpiryDate to true by default
                this.ShowTransferFrequency = true;
                this.ShowTransferExpiryDate = true;

                //Check the transfer type and determine if we need to show the frequency and expiry date
                const transferTypeDU = this.ModelDataUnit_Get({ id: "INP_TransferType" });
                if (!this.globalFunctions.isEmpty(transferTypeDU) && !this.globalFunctions.isEmpty(transferTypeDU.Value)) {
                    if (transferTypeDU.Value === "Immediate" || transferTypeDU.Value === "One-Off") {

                        //If the transfer type is Immediate or One-Off, hide the frequency and expiry date
                        this.ShowTransferFrequency = false;
                        this.ShowTransferExpiryDate = false;
                    }
                }

                //Set the TransferToList with the initial TransferFromAccountOptions
                this.TransferToAccountOptions = JSON.parse(JSON.stringify(this.TransferFromAccountOptions));

                //Check if the transfer from account is set
                const trasferFromDU = this.ModelDataUnit_Get({ id: "INP_TransferFrom" });
                if (!this.globalFunctions.isEmpty(trasferFromDU) && !this.globalFunctions.isEmpty(trasferFromDU.Value)) {

                    //Find the matching account in the TransferToAccountOptions
                    const matchingAccount = this.TransferToAccountOptions.filter(x => x.ControlGUID === trasferFromDU.Value)[0];
                    if (!this.globalFunctions.isEmpty(matchingAccount)) {

                        //Remove the selected transfer from account from the transfer to options. We do this to prevent the user from selecting the same account for both transfer from and transfer to.
                        const fieldIndex = this.TransferToAccountOptions.findIndex(x => x.ControlGUID === matchingAccount.ControlGUID);
                        this.TransferToAccountOptions.splice(fieldIndex, 1);
                    }
                }

                //Check if the frequency is selected
                const trasferFrequencyDU = this.ModelDataUnit_Get({ id: "INP_Frequency" });
                if (!this.globalFunctions.isEmpty(trasferFrequencyDU) && !this.globalFunctions.isEmpty(trasferFrequencyDU.Value) && trasferFrequencyDU.Value === "Once") {

                    //If the frequency is set to once, hide the expiry date
                    this.ShowTransferExpiryDate = false;
                }

                //Check if the checkbox for Due Until Further Notice is checked
                const dueUntilFurtherNoticeDU = this.ModelDataUnit_Get({ id: "INP_DueUntilFurtherNotice" });
                if (!this.globalFunctions.isEmpty(dueUntilFurtherNoticeDU) && dueUntilFurtherNoticeDU.IsChecked) {

                    //If the checkbox is checked, set the expiry date to null and disable the expiry date input
                    const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_ExpiryDate" });
                    if (!this.globalFunctions.isEmpty(expiryDateDU)) {
                        expiryDateDU.Value = null;
                        expiryDateDU.Disabled = true;
                    }
                }
                else {

                    //If the checkbox is unchecked, enable the expiry date input
                    const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_ExpiryDate" });
                    if (!this.globalFunctions.isEmpty(expiryDateDU)) {
                        expiryDateDU.Disabled = false;
                    }
                }
            }

            //For Customer Nominated Payment section
            if (this.ShowCustomerNominatedPaymentModify) {
                // Set the ShowPaymentExpiryDate to false by default
                this.ShowPaymentExpiryDate = false;

                // Check if the payment frequency is selected
                const paymentFrequencyDU = this.ModelDataUnit_Get({ id: "INP_PaymentFrequency" });
                if (!this.globalFunctions.isEmpty(paymentFrequencyDU) && !this.globalFunctions.isEmpty(paymentFrequencyDU.Value)) {

                    //Get the matching frequency option
                    const selectedFrequencyOption = this.CustomerNominatedFrequencyOptions.find(option => option.ControlGUID === paymentFrequencyDU.Value);
                    if (!this.globalFunctions.isEmpty(selectedFrequencyOption) && !this.globalFunctions.isEmpty(selectedFrequencyOption.ControlValue)) {
                        if (selectedFrequencyOption.Classifications.toLowerCase().includes("one off") === true) {
                            // If frequency is "Once", hide expiry date and disable the until further notice checkbox
                            this.ShowPaymentExpiryDate = false;

                            // Find and disable the until further notice checkbox
                            const untilFurtherNoticeDU = this.ModelDataUnit_Get({ id: "INP_UntilFurtherNotice" });
                            if (!this.globalFunctions.isEmpty(untilFurtherNoticeDU)) {
                                untilFurtherNoticeDU.IsChecked = false;
                                untilFurtherNoticeDU.Disabled = true;
                            }

                            // Set the expiry date to null
                            const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_PaymentExpiryDate" });
                            if (!this.globalFunctions.isEmpty(expiryDateDU)) {
                                expiryDateDU.Value = null;
                                expiryDateDU.ValueDisplay = "";
                            }
                        } else {

                            //Show expiry date
                            this.ShowPaymentExpiryDate = true;

                            // For recurring payments, enable the until further notice checkbox
                            const untilFurtherNoticeDU = this.ModelDataUnit_Get({ id: "INP_UntilFurtherNotice" });
                            if (!this.globalFunctions.isEmpty(untilFurtherNoticeDU)) {
                                untilFurtherNoticeDU.Disabled = false;
                            }

                            // Check if until further notice is checked
                            if (!this.globalFunctions.isEmpty(untilFurtherNoticeDU) && untilFurtherNoticeDU.IsChecked) {
                                // If checked, disable expiry date and set to null
                                const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_PaymentExpiryDate" });
                                if (!this.globalFunctions.isEmpty(expiryDateDU)) {
                                    expiryDateDU.Value = null;
                                    expiryDateDU.ValueDisplay = "";
                                    expiryDateDU.Disabled = true;
                                }
                            } else {
                                // If not checked, enable expiry date
                                const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_PaymentExpiryDate" });
                                if (!this.globalFunctions.isEmpty(expiryDateDU)) {
                                    expiryDateDU.Disabled = false;
                                }
                            }
                        }
                    }
                }
            }

            //For Scheduled Payment section
            if (this.ShowScheduledPaymentModify) {

                //Handle payment type changes
                const paymentMethodDU = this.ModelDataUnit_Get({ id: "INP_PaymentMethod" });
                if (!this.globalFunctions.isEmpty(paymentMethodDU) && !this.globalFunctions.isEmpty(paymentMethodDU.Value)) {

                    //Get references to the relevant form fields
                    const totalAmountDU = this.ModelDataUnit_Get({ id: "INP_TotalAmount" });
                    const directDebitAmountDU = this.ModelDataUnit_Get({ id: "INP_DirectDebitAmount" });

                    //Minimum monthly Payment Amount
                    let minimumPaymentAmount = JSON.parse(JSON.stringify(this.MonthlyRepaymentAmount));
                    let additionalAmount = 0;

                    // Set the frequency options
                    const frequencyDU = this.ModelDataUnit_Get({ id: "INP_ScheduledFrequency" });
                    if (!this.globalFunctions.isEmpty(frequencyDU) && !this.globalFunctions.isEmpty(frequencyDU.Value)) {
                        //For Scheduled Payment, we only allow Weekly, Fortnightly, and Monthly. All the payments are calculated in the server

                        //Find the selected frequency option
                        const selectedFrequencyOption = this.ScheduleFrequencyOptions.find(option => option.ControlGUID === frequencyDU.Value);
                        if (!this.globalFunctions.isEmpty(selectedFrequencyOption)) {

                            //Check if the selected frequency is Weekly or Fortnightly and adjust the minimum payment amount accordingly
                            if (selectedFrequencyOption.ControlDisplay.toLowerCase().includes("fortnightly") === true) {

                                //For Fortnightly, we can set the amount to half of the monthly instalment
                                minimumPaymentAmount = this.FortnightlyRepaymentAmount;
                            }
                            else if (selectedFrequencyOption.ControlDisplay.toLowerCase().includes("weekly") === true) {

                                //For Weekly, we can set the amount to one quarter of the monthly instalment
                                minimumPaymentAmount = this.WeeklyRepaymentAmount;
                            }

                            //Otherwise, it remains as Monthly
                        }

                        //Set the scheduled amount
                        directDebitAmountDU.Value = minimumPaymentAmount.toString();
                        directDebitAmountDU.ValueDisplay = this.globalFunctions.customDataTypeParser(minimumPaymentAmount, "currency.2", "aus");
                    }

                    // For "Scheduled" payment type, disable the direct debit amount field
                    if (paymentMethodDU.Value === "Scheduled") {

                        //Show the minimum payment amount field
                        this.ShowMinimumPaymentAmount = true;

                        //Hide additional fields for "Scheduled" payment type
                        this.ShowAdditionalPaymentInput = false;
                        this.ShowCustomerNominatedInput = false;
                        this.ShowTotalDDAmount = false;

                        // Set the description message for Scheduled payment type
                        this.PaymentTypeDescriptionMessage = "Collect the minimum monthly instalment amount. Monthly instalment is divided by 2 for fortnightly and 4 for weekly frequencies.";
                    }

                    // For "Customer Nominated" payment type, enable the direct debit amount field
                    else if (paymentMethodDU.Value === "Fixed Amount") {

                        //Show the customer nominated input field
                        this.ShowCustomerNominatedInput = true;

                        //Hide other payment input fields
                        this.ShowAdditionalPaymentInput = false;
                        this.ShowTotalDDAmount = false;
                        this.ShowMinimumPaymentAmount = false;

                        // Set the description message for Customer Nominated payment type
                        this.PaymentTypeDescriptionMessage = "Collect a fixed amount.";
                    }

                    // For "Required Payment + Additional + Fee + GST" payment type
                    else if (paymentMethodDU.Value === "Minimum") {

                        //Show the input fields
                        this.ShowAdditionalPaymentInput = true;
                        this.ShowTotalDDAmount = true;
                        this.ShowMinimumPaymentAmount = true;

                        //Hide the customer nominated input field
                        this.ShowCustomerNominatedInput = false;

                        // Set the description message for Required Payment + Additional + Fee + GST payment type
                        this.PaymentTypeDescriptionMessage = "Collect a fixed amount in addition to the monthly instalment amount and regular account management fees. If the monthly instalment changes, the total collected will change but the fixed amount will not change.";

                        //TODO: Grab the data units for scheduled amount and additional amount
                        if (!this.globalFunctions.isEmpty(totalAmountDU)) {

                            const additionalAmountDU = this.ModelDataUnit_Get({ id: "INP_AdditionalAmount" });
                            if (!this.globalFunctions.isEmpty(additionalAmountDU) && !this.globalFunctions.isEmpty(additionalAmountDU.Value)) {

                                additionalAmount = parseFloat(additionalAmountDU.Value);
                            }
                        }

                    }

                    //Calculate the total direct debit amount
                    const totalDDAmount = minimumPaymentAmount + additionalAmount;
                    totalAmountDU.Value = totalDDAmount.toString();
                    totalAmountDU.ValueDisplay = this.globalFunctions.customDataTypeParser(totalDDAmount, "currency.2", "aus");
                }
            }
        });
    }

    //Method to save bank account details
    public BankAccount_Save(): void {

        //Get the input values
        const bsbNumberDU = this.ModelDataUnit_Get({ id: "INP_BSBNumber" });
        const accountNumberDU = this.ModelDataUnit_Get({ id: "INP_AccountNumber" });
        const accountNameDU = this.ModelDataUnit_Get({ id: "INP_AccountName" });

        //Validate required fields
        if (this.globalFunctions.isEmpty(bsbNumberDU) || this.globalFunctions.isEmpty(bsbNumberDU.Value)) {
            this.notifyService.Warning_Show("BSB Number is required", "Validation Error");
            return;
        }

        if (this.globalFunctions.isEmpty(accountNumberDU) || this.globalFunctions.isEmpty(accountNumberDU.Value)) {
            this.notifyService.Warning_Show("Account Number is required", "Validation Error");
            return;
        }

        if (this.globalFunctions.isEmpty(accountNameDU) || this.globalFunctions.isEmpty(accountNameDU.Value)) {
            this.notifyService.Warning_Show("Account Name is required", "Validation Error");
            return;
        }

        //Bank Account data to be saved
        const bankAccountData = {
            AccountBSBNo: bsbNumberDU.Value,
            AccountNumber: accountNumberDU.Value,
            AccountName: accountNameDU.Value
        };

        // Check if we have existing bank account details (editing mode)
        let bankAccountGUID = "{NewGUID}";

        if (!this.globalFunctions.isEmpty(this.BankAccountDetails)) {
            bankAccountGUID = this.BankAccountDetails.BankDetailGUID;

            // Compare submitted values with original values
            if (this.BankAccountDetails.AccountBSBNo == bankAccountData.AccountBSBNo
                && this.BankAccountDetails.AccountNumber == bankAccountData.AccountNumber
                && (this.BankAccountDetails.AccountName != null ? this.BankAccountDetails.AccountName.toLowerCase().trim() : this.BankAccountDetails.AccountName) == bankAccountData.AccountName.toLowerCase().trim()) {
                this.notifyService.Error_Show("No changes detected in Bank Details", "Validation Error");
                return;
            }
        }

        //Prepare the API request payload
        const apiRequest = {
            AccountID: this.TargetAccountID,
            BankDetailGUID: bankAccountGUID,
            AccountName: bankAccountData.AccountName,
            AccountBSBNo: bankAccountData.AccountBSBNo,
            AccountNumber: bankAccountData.AccountNumber
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.SaveDirectDebitBankAccount], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }
                else {

                    //Get the response
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Get updated bank account details from the response
                    let updatedBankDetails = null;
                    if (!this.globalFunctions.isEmpty(response.DirectDebitBankAccount) && response.DirectDebitBankAccount.AccountBSBNo != null) {
                        updatedBankDetails = response.DirectDebitBankAccount;

                        //Removes ARMNet type of encoding from a string
                        updatedBankDetails.AccountName = this.globalFunctions.HTMLUnescape(updatedBankDetails.AccountName);
                        updatedBankDetails.BankName = this.globalFunctions.HTMLUnescape(updatedBankDetails.BankName);
                    }

                    //Show toast message on success
                    this.notifyService.Success_Show("The bank details have been saved successfully", "Save Bank Details");

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;

                    //Close the dialog with the updated bank account details from API response
                    const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];
                    if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                        matchingModal.DialogRef.close({ updatedBankAccountDetails: updatedBankDetails });
                    } else {
                        this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                    }
                }
            });
    }

    //Method to process inter account transfer
    public ProcessInterAccountTransfer_Submit(): void {

        //Get input values
        const sourceAccountDU = this.ModelDataUnit_Get({ id: "INP_TransferFrom" });
        const destinationAccountDU = this.ModelDataUnit_Get({ id: "INP_TransferTo" });
        const transferAmountDU = this.ModelDataUnit_Get({ id: "INP_TransferAmount" });
        const transferTypeDU = this.ModelDataUnit_Get({ id: "INP_TransferType" });
        const frequencyDU = this.ModelDataUnit_Get({ id: "INP_Frequency" });
        const effectiveDateDU = this.ModelDataUnit_Get({ id: "INP_DueDate" });
        const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_ExpiryDate" });
        const untilFurtherNoticeDU = this.ModelDataUnit_Get({ id: "INP_DueUntilFurtherNotice" });

        if (transferTypeDU.Value === "Immediate") {
            if (this.globalFunctions.isEmpty(frequencyDU) || this.globalFunctions.isEmpty(frequencyDU.Value)) {
                this.notifyService.Warning_Show("Immediate transfer feature isn't supported yet", "Error");
                return;
            }
        }

        //Validate inputs
        if (this.globalFunctions.isEmpty(sourceAccountDU) || this.globalFunctions.isEmpty(sourceAccountDU.Value)) {
            this.notifyService.Warning_Show("Source account is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(destinationAccountDU) || this.globalFunctions.isEmpty(destinationAccountDU.Value)) {
            this.notifyService.Warning_Show("Destination account is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(transferAmountDU) || this.globalFunctions.isEmpty(transferAmountDU.Value)) {
            this.notifyService.Warning_Show("Transfer amount is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(transferTypeDU) || this.globalFunctions.isEmpty(transferTypeDU.Value)) {
            this.notifyService.Warning_Show("Transfer type is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(effectiveDateDU) || this.globalFunctions.isEmpty(effectiveDateDU.Value)) {
            this.notifyService.Warning_Show("Effective date is required", "Error");
            return;
        }

        // Frequency is required unless transfer type is Immediate or One-Off
        if (transferTypeDU.Value !== "Immediate" && transferTypeDU.Value !== "One-Off") {
            if (this.globalFunctions.isEmpty(frequencyDU) || this.globalFunctions.isEmpty(frequencyDU.Value)) {
                this.notifyService.Warning_Show("Frequency is required", "Error");
                return;
            }

            // Expiry date is required for recurring payments unless "until further notice" is checked
            if (frequencyDU.Value !== "Once" && !untilFurtherNoticeDU.IsChecked) {
                if (this.globalFunctions.isEmpty(expiryDateDU) || this.globalFunctions.isEmpty(expiryDateDU.Value)) {
                    this.notifyService.Warning_Show("Expiry date is required for recurring payments", "Error");
                    return;
                } else if (effectiveDateDU.Value > expiryDateDU.Value) {
                    this.notifyService.Warning_Show("Expiry date cannot be set prior to Effective date", "Error");
                    return;
                }
            }
        }

        //API Request
        const apiRequest = {
            AccountID: this.TargetAccountID,
            SourceAccountID: sourceAccountDU.Value,
            DestinationAccountID: destinationAccountDU.Value,
            TransferAmount: parseFloat(transferAmountDU.Value),
            TransferType: transferTypeDU.Value,
            EffectiveDate: effectiveDateDU.Value,
            Frequency: this.globalFunctions.isEmpty(frequencyDU) ? null : frequencyDU.Value,
            ExpiryDate: !this.globalFunctions.isEmpty(untilFurtherNoticeDU) && untilFurtherNoticeDU.IsChecked ? "01-Jan-2199" : (!this.globalFunctions.isEmpty(expiryDateDU) ? expiryDateDU.Value : null)
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        this.apiService.APIData_Post(this.apiService.Endpoints.PaymentManagementController, PaymentManagementControllerMethods[PaymentManagementControllerMethods.ProcessInterAccountTransfer], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {
                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {

                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Updated linked accounts from the server
                    const updatedLinkedAccounts = response.PaymentManagementLinkedAccounts;

                    // Find the modal in the FeatureModals collection
                    const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];

                    if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                        // Close the dialog with the updated linked accounts data
                        // The parent component will receive this data in the afterClosed() event
                        matchingModal.DialogRef.close({ updatedLinkedAccounts: updatedLinkedAccounts });
                    } else {

                        // Close the modal the normal way if we couldn't find it in the feature modals
                        this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;

                    let message = ( apiRequest.TransferType === "Recurring") ? `A ${apiRequest.Frequency.toLowerCase()} interaccount transfer has been scheduled`
                                                                            : `A one-off interaccount transfer has been scheduled`;
                    // Show the popup toast notification
                    this.notifyService.Success_Show(message, "Interaccount Transfer scheduled");

                    return;
                }
            });
    }

    //Method to save customer nominated payment details
    public CustomerNominatedPayment_Save(): void {
        //Get input values
        const amountDU = this.ModelDataUnit_Get({ id: "INP_PaymentAmount" });
        const frequencyDU = this.ModelDataUnit_Get({ id: "INP_PaymentFrequency" });
        const nextPaymentDateDU = this.ModelDataUnit_Get({ id: "INP_NextPaymentDate" });
        const expiryDateDU = this.ModelDataUnit_Get({ id: "INP_PaymentExpiryDate" });
        const untilFurtherNoticeDU = this.ModelDataUnit_Get({ id: "INP_UntilFurtherNotice" });
        const paymentTxnDU = this.ModelDataUnit_Get({ id: "INP_PaymentTxn" });
        const chargeTxnDU = this.ModelDataUnit_Get({ id: "INP_ChargeTxn" });
        const noteDU = this.ModelDataUnit_Get({ id: "INP_Note" });

        //Validate required fields
        if (this.globalFunctions.isEmpty(amountDU) || this.globalFunctions.isEmpty(amountDU.Value)) {
            this.notifyService.Warning_Show("Payment amount is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(frequencyDU) || this.globalFunctions.isEmpty(frequencyDU.Value)) {
            this.notifyService.Warning_Show("Payment frequency is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(nextPaymentDateDU) || this.globalFunctions.isEmpty(nextPaymentDateDU.Value)) {
            this.notifyService.Warning_Show("Next payment date is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(paymentTxnDU) || this.globalFunctions.isEmpty(paymentTxnDU.Value)) {
            this.notifyService.Warning_Show("Payment transaction is required", "Error");
            return;
        }

        //Get the matching frequency option
        const selectedFrequencyOption = this.CustomerNominatedFrequencyOptions.find(option => option.ControlGUID === frequencyDU.Value);
        if (!this.globalFunctions.isEmpty(selectedFrequencyOption) && !this.globalFunctions.isEmpty(selectedFrequencyOption.ControlValue)) {

            // Expiry date is required for recurring payments unless "until further notice" is checked
            if (selectedFrequencyOption.Classifications.toLowerCase().includes("one off") === false && !untilFurtherNoticeDU.IsChecked) {
                if (this.globalFunctions.isEmpty(expiryDateDU) || this.globalFunctions.isEmpty(expiryDateDU.Value)) {
                    this.notifyService.Warning_Show("Expiry date is required for ongoing payments", "Error");
                    return;
                } else if (nextPaymentDateDU.Value > expiryDateDU.Value) {
                    this.notifyService.Warning_Show("Expiry date cannot be set prior to Next payment date", "Error");
                    return;
                }        
            }
        }


        // Determine the expiry date value
        let expiryDateValue = null;
        if (!this.globalFunctions.isEmpty(expiryDateDU) && !this.globalFunctions.isEmpty(expiryDateDU.Value)) {
            expiryDateValue = expiryDateDU.Value;
        }

        // Check If "until further notice" is checked
        let paymentNotExpiredUntilFurtherNotice = false;
        if (!this.globalFunctions.isEmpty(untilFurtherNoticeDU)) {
            paymentNotExpiredUntilFurtherNotice = untilFurtherNoticeDU.IsChecked;
        }

        // Remove the filler "Not Applicable" if it was in place, so it does not affect the save, see XV8-5792
        let chargeTxnDUValue = chargeTxnDU.Value;

        if (chargeTxnDUValue === "Not Applicable") {
            chargeTxnDUValue = "";
        }

        //Create API payment data object
        const apiRequest = {
            AccountID: this.TargetAccountID,
            PaymentType: 'Customer Nominated',
            FrequencyGUID: frequencyDU.Value,
            CustomerNominatedAmount: parseFloat(amountDU.Value),
            NextPaymentDate: nextPaymentDateDU.Value,
            PaymentTransactionGUID: paymentTxnDU.Value,
            ChargeTransactionGUID: chargeTxnDUValue,
            Notes: noteDU.Value,
            ExpiryDate: expiryDateValue,
            PaymentNotExpiredUntilFurtherNotice: paymentNotExpiredUntilFurtherNotice
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.SaveCustomerNominatedPayment], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {
                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {

                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    if (response !== null && response.UpdatedScheduledPaymentDetails !== null) {

                        //Construct a new payment object with the updated details
                        const updatedScheduledPaymentDetails = response.UpdatedScheduledPaymentDetails;

                        // Search for the modal in the FeatureModals collection
                        const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];

                        if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                            // Close the dialog with the updated payment data
                            matchingModal.DialogRef.close({ updatedPaymentDetails: updatedScheduledPaymentDetails });

                        } else {

                            // Show success message and close modal           
                            this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                        }

                        //Show success notification
                        this.notifyService.Success_Show("Customer Nominated Payment details saved successfully", "Scheduled Payment");
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }

    //Method to save scheduled payment details with confirmation
    public ScheduledPayment_Save(): void {
        //Get input values
        const paymentMethodDU = this.ModelDataUnit_Get({ id: "INP_PaymentMethod" });
        const directDebitDU = this.ModelDataUnit_Get({ id: "INP_TotalAmount" });
        const additionalAmountDU = this.ModelDataUnit_Get({ id: "INP_AdditionalAmount" });
        const customerNominatedAmountDU = this.ModelDataUnit_Get({ id: "INP_CustomerNominatedAmount" });
        const totalAmountDU = this.ModelDataUnit_Get({ id: "INP_TotalAmount" });
        const frequencyDU = this.ModelDataUnit_Get({ id: "INP_ScheduledFrequency" });
        const nextPaymentDateDU = this.ModelDataUnit_Get({ id: "INP_ScheduledNextPaymentDate" });
        const noteDU = this.ModelDataUnit_Get({ id: "INP_Note" });

        // Get the payment type
        if (this.globalFunctions.isEmpty(paymentMethodDU) || this.globalFunctions.isEmpty(paymentMethodDU.Value)) {
            this.notifyService.Warning_Show("Payment method is required", "Error");
            return;
        }

        //Validate required fields
        if (this.globalFunctions.isEmpty(directDebitDU) || this.globalFunctions.isEmpty(directDebitDU.Value)) {
            this.notifyService.Warning_Show("Direct Debit Amount is required", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(frequencyDU) || this.globalFunctions.isEmpty(frequencyDU.Value)) {
            this.notifyService.Warning_Show("Payment frequency is required", "Error");
            return;
        }

        // Ensure the selected frequency is valid
        const selectedFrequencyOption = this.ScheduleFrequencyOptions.find(option => option.ControlGUID === frequencyDU.Value);
        if (this.globalFunctions.isEmpty(selectedFrequencyOption)) {
            this.notifyService.Warning_Show("Payment frequency is invalid", "Error");
            return;
        }
        // Validate that frequency is one of the allowed values: Weekly, Fortnightly, or Monthly
        // This ensures we don't allow "Once" for scheduled payments
        if (selectedFrequencyOption.ControlDisplay.includes("Once") === true) {
            this.notifyService.Warning_Show("Frequency must be Weekly, Fortnightly, or Monthly for scheduled payments", "Error");
            return;
        }

        if (this.globalFunctions.isEmpty(nextPaymentDateDU) || this.globalFunctions.isEmpty(nextPaymentDateDU.Value)) {
            this.notifyService.Warning_Show("Next payment date is required", "Validation Error");
            return;
        }        // If payment type is RequiredPayment, validate additionalAmount
        if (paymentMethodDU.Value === "RequiredPayment") {
            if (this.globalFunctions.isEmpty(additionalAmountDU) || this.globalFunctions.isEmpty(additionalAmountDU.Value)) {
                this.notifyService.Warning_Show("Additional Payment amount is required for this payment type", "Validation Error");
                return;
            }
        }

        // Format dates for display
        const formatDate = (date: Date | null): string | null => {
            if (date === null || date === undefined) return null;

            const day = date.getDate();
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
        };

        //Calculate the direct debit amount based on payment type
        let directDebitAmount: number = parseFloat(directDebitDU.Value);
        if (isNaN(directDebitAmount)) directDebitAmount = 0;

        let paymentMethod: string = paymentMethodDU.Value;

        const note: string = noteDU.Value;

        let customerNominatedAmount: number = parseFloat(customerNominatedAmountDU.Value);
        if (isNaN(customerNominatedAmount)) customerNominatedAmount = 0;

        let additionalAmount: number = parseFloat(additionalAmountDU.Value);
        if (isNaN(additionalAmount)) additionalAmount = 0;

        //Set the payment method
        paymentMethod = paymentMethodDU.Value;

        // For RequiredPayment, include additional amount in the total: Required Payment + Additional + Fee + GST
        if (paymentMethod === "minimum") {
            // Use the pre-calculated total amount
            const parsedTotal = parseFloat(totalAmountDU.Value);
            if (!isNaN(parsedTotal)) {
                directDebitAmount = parsedTotal;
            }
        }

        // For CustomerNominated, use the customer nominated amount directly
        else if (paymentMethod === "Fixed Amount") {

            // Use the pre-calculated total amount
            const parsedCustomerNominated = parseFloat(customerNominatedAmountDU.Value);
            if (!isNaN(parsedCustomerNominated)) {
                directDebitAmount = parsedCustomerNominated;
            }

        }

        //Do a confirm screen for this, similar to disable payment
        const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Confirm Modal", 0, true, false);

        //Use html content so that we can style it
        dialogRef.DialogRef.componentInstance.htmlContent = "Please note that saving these changes will delete any existing scheduled payment and create a new one. Do you want to continue?";

        dialogRef.DialogRef.afterClosed().subscribe(result => {
            if (result === true) {

                // Prepare API request
                const apiRequest = {
                    GUID: "{NewGUID}",
                    AccountID: this.TargetAccountID,
                    ProductLoanTransferGUID: null,
                    PaymentType: "Scheduled",
                    PaymentMethod: paymentMethod,
                    Frequency: frequencyDU.Value,
                    NextPaymentDate: formatDate(new Date(nextPaymentDateDU.Value)),
                    CustomerNominatedAmount: customerNominatedAmount,
                    AdditionalAmount: additionalAmount,
                    Note: note
                };


                this.IsGenerateInProgress = true;
                this.clientDataStore.SetShowFullscreenLoading(true);

                this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.SaveScheduledPayment], apiRequest)
                    .subscribe(apiResponse => {

                        //Check for empty response
                        if (this.globalFunctions.isEmpty(apiResponse)) {

                            //Turn off the spinner
                            this.IsGenerateInProgress = false;
                            this.clientDataStore.SetShowFullscreenLoading(false);
                            return;
                        }

                        const response = JSON.parse(JSON.stringify(apiResponse));
                        if (response !== null && response.UpdatedScheduledPaymentDetails !== null) {

                            // Pass updated payment details back to parent
                            const result = {
                                updatedPaymentDetails: response.UpdatedScheduledPaymentDetails
                            };

                            // Close modal and pass result
                            const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];
                            if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                                matchingModal.DialogRef.close(result);
                            } else {
                                this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                            }

                            //Turn off the spinner
                            this.IsGenerateInProgress = false;
                            this.clientDataStore.SetShowFullscreenLoading(false);

                            //Display the success message
                            this.notifyService.Success_Show("Scheduled payment details updated successfully", "Scheduled Payment");
                        }
                        else {
                            this.notifyService.Error_Show("Something went wrong, please contact AMAL Servicing Team.", "Scheduled Payment");
                        }
                    });
            }
        });
    }

    //Method to save deferred payment details
    public DeferPayment_Save(): void {

        // Get input values
        const deferMonthsDU = this.ModelDataUnit_Get({ id: "INP_DeferMonths" });
        const selectedPayment = this.SelectedPaymentDetails;

        if (this.globalFunctions.isEmpty(deferMonthsDU) || this.globalFunctions.isEmpty(deferMonthsDU.Value)) {
            this.notifyService.Warning_Show("Number of months to defer is required", "Error");
            return;
        }
        if (this.globalFunctions.isEmpty(selectedPayment) || this.globalFunctions.isEmpty(selectedPayment.GUID)) {
            this.notifyService.Warning_Show("No payment selected to defer", "Error");
            return;
        }

        // Prepare API request
        const apiRequest = {
            AccountID: this.TargetAccountID,
            NumMonthsToDefer: deferMonthsDU.Value,
            ProductLoanTransferGUID: selectedPayment.GUID,
        };

        //Turn on the spinner
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Invoke API to defer the payment
        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.DeferScheduledPayment], apiRequest)
            .subscribe(apiResponse => {

                //Check for empty response
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //Turn off the spinner
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }

                //Get the response
                const response = JSON.parse(JSON.stringify(apiResponse));

                if (response != null && response.NextPaymentDate != null) {

                    // Pass updated payment details and new date back to parent
                    const result = {
                        deferredPaymentDetails: {
                            UpdatedPaymentDetails: selectedPayment,
                            DeferMonths: deferMonthsDU.Value,
                            NextPaymentDate: response.NextPaymentDate
                        }
                    };

                    //Turn off the spinner
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    // Show success notification before closing
                    this.notifyService.Success_Show("Payment has been deferred successfully", "Defer Payment");

                    // Close modal and pass result
                    const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];
                    if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                        matchingModal.DialogRef.close(result);
                    } else {
                        this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                    }
                } else {
                    this.notifyService.Error_Show("Something went wrong, please contact AMAL Servicing.", "Error");
                }
            });
    }

    //Method to save changed frequency
    public ChangeFrequency_Save(): void {

        // Get input values
        const newFrequencyDU = this.ModelDataUnit_Get({ id: "INP_NewFrequency" });
        const selectedPayment = this.SelectedPaymentDetails;

        //Validate required fields
        if (this.globalFunctions.isEmpty(newFrequencyDU) || this.globalFunctions.isEmpty(newFrequencyDU.Value)) {
            this.notifyService.Warning_Show("New frequency is required", "Error");
            return;
        }
        if (this.globalFunctions.isEmpty(selectedPayment) || this.globalFunctions.isEmpty(selectedPayment.GUID)) {
            this.notifyService.Warning_Show("No payment selected to change frequency", "Error");
            return;
        }

        // Prepare API request
        const apiRequest = {
            AccountID: this.TargetAccountID,
            ScheduledPaymentGUID: selectedPayment.GUID,
            PaymentType: "Customer Nominated",
            ModifyType: "Update",
            TargetEntityPropertyName: "Frequency",
            TargetEntityPropertyValue: newFrequencyDU.Value
        };

        //Turn on the spinner
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Invoke API to change the frequency
        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.ModifyScheduledPayment], apiRequest)
            .subscribe(apiResponse => {

                //Check for empty response
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //Turn off the spinner
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }

                //Get the response
                const response = JSON.parse(JSON.stringify(apiResponse));
                if (response !== null && response.UpdatedScheduledPaymentDetails !== null) {

                    // Pass updated payment details back to parent
                    const result = {
                        updatedFrequencyDetails: {
                            UpdatedPaymentDetails: response.UpdatedScheduledPaymentDetails
                        }
                    };

                    //Turn off the spinner
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    //Display success message
                    this.notifyService.Success_Show("Payment frequency has been changed successfully", "Payment Frequency");

                    // Close modal and pass result
                    const matchingModal = this.globalFunctions.FeatureModals.filter(x => x.GUID === this.ModalIdentifier)[0];
                    if (!this.globalFunctions.isEmpty(matchingModal) && !this.globalFunctions.isEmpty(matchingModal.DialogRef)) {
                        matchingModal.DialogRef.close(result);
                    } else {
                        this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
                    }
                } else {
                    this.notifyService.Error_Show("Something went wrong, please contact AMAL Servicing Team.", "Error");
                }
            });
    }
}
